"use client";
import { Box, Button, Chip, Container, Stack, Typography } from "@mui/material";
import { ConstructionIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { BasemakerLogo } from "@/components/basemaker/logo";
import { BoxWithBackgroundImage } from "@/components/ui/box-with-background-image";
import { Motion } from "@/components/ui/motion";
import { MotionTypography } from "@/components/ui/motion/typography";

export default function NotFound() {
	const router = useRouter();

	return (
		<Box
			sx={{
				backgroundImage:
					"linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)",
				position: "relative",
				minHeight: "100vh",
			}}
		>
			<Container>
				<Stack
					sx={{
						alignItems: "center",
						gap: { xs: 6, md: 8 },
						justifyContent: "center",
						minHeight: "100svh",
						py: { xs: 8, md: 10 },
						position: "relative",
						zIndex: 2,
					}}
				>
					<Motion
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
					>
						<BasemakerLogo sx={{ maxWidth: { xs: "10rem", md: "14rem" } }} />
					</Motion>

					<Stack
						sx={{
							alignItems: "center",
							backgroundColor: "background.paper",
							borderRadius: 4,
							gap: { xs: 3, md: 4 },
							maxWidth: { xs: "90%", md: "50rem" },
							p: { xs: 5, md: 7 },
							textAlign: "center",
							width: "100%",
							boxShadow:
								"0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.04)",
							border: "1px solid rgba(0, 0, 0, 0.06)",
						}}
					>
						<Stack sx={{ gap: 3, alignItems: "center" }}>
							<Motion
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ delay: 0.2, duration: 0.5 }}
							>
								<Typography
									component="h1"
									variant="website"
									sx={{
										fontSize: { xs: "2rem", md: "2.75rem" },
										fontWeight: 700,
										textAlign: "center",
										lineHeight: 1.2,
										color: "grey.900",
									}}
								>
									Ops! Parece que você se perdeu
								</Typography>
							</Motion>

							<Motion
								initial={{ scale: 0, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								transition={{ duration: 0.5, delay: 0.3 }}
							>
								<Chip
									icon={<ConstructionIcon />}
									label="Plataforma renovada"
									color="followers"
									variant="outlined"
									sx={{
										fontSize: "0.9rem",
										height: "2.25rem",
										fontWeight: 600,
										px: 1,
										boxShadow: "0 2px 8px rgba(102, 51, 153, 0.12)",
										border: "1px solid rgba(102, 51, 153, 0.3)",
										backgroundColor: "rgba(102, 51, 153, 0.08)",
										color: "audiences.main",
										"& .MuiChip-icon": {
											fontSize: "1rem",
											color: "audiences.main",
										},
										"&:hover": {
											boxShadow: "0 4px 12px rgba(102, 51, 153, 0.18)",
											transform: "translateY(-1px)",
										},
										transition: "all 0.3s ease",
									}}
								/>
							</Motion>

							<MotionTypography
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.5, duration: 0.5 }}
								variant="bodyLarge"
								sx={{ lineHeight: 1.7, fontWeight: 400, color: "grey.700" }}
							>
								<strong>As coisas mudaram por aqui!</strong> Nossa antiga
								landing page foi completamente renovada. Agora temos uma{" "}
								<Typography
									component="span"
									sx={{ color: "audiences.main", fontWeight: 600 }}
								>
									plataforma moderna e intuitiva
								</Typography>{" "}
								onde você pode solicitar públicos qualificados de forma muito
								mais simples.
							</MotionTypography>

							<MotionTypography
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.7, duration: 0.5 }}
								variant="body1"
								sx={{ color: "grey.600", lineHeight: 1.6 }}
							>
								Se você estava procurando por tutoriais ou conteúdos do nosso
								antigo site, não se preocupe! Nossa nova plataforma é ainda mais
								fácil de usar e oferece uma experiência completa para
								anunciantes.
							</MotionTypography>
						</Stack>

						<Stack
							sx={{
								gap: { xs: 3, md: 4 },
								width: "100%",
								alignItems: "center",
								mt: 2,
							}}
						>
							<Motion
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.9, duration: 0.5 }}
							>
								<Button
									variant="contained"
									color="audiences"
									size="large"
									onClick={() => router.push("/")}
								>
									Voltar para a página inicial
								</Button>
							</Motion>
						</Stack>

						<Motion
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 1.1, duration: 0.5 }}
							sx={{
								backgroundColor: "rgba(102, 51, 153, 0.04)",
								borderRadius: 3,
								p: { xs: 3, md: 4 },
								width: "100%",
								mt: 1,
								border: "1px solid rgba(102, 51, 153, 0.08)",
							}}
						>
							<Typography
								variant="body2"
								sx={{
									color: "grey.700",
									fontWeight: 500,
									lineHeight: 1.6,
									fontSize: "0.95rem",
								}}
							>
								💡 <strong>Nova experiência:</strong> Agora você pode testar
								nossa plataforma gratuitamente por 14 dias e ver como é fácil
								encontrar públicos qualificados para seus anúncios.
							</Typography>
						</Motion>
					</Stack>
				</Stack>
			</Container>

			<BoxWithBackgroundImage
				sx={{
					backgroundImage: "url(/images/website/background-hero.svg)",
					backgroundSize: "100% 80%",
					inset: 0,
					opacity: 0.05,
					position: "absolute",
				}}
			/>
		</Box>
	);
}
